import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { ngClassObject } from '../../../types';
import { IconsModule } from '../../icons/icons.module';
import { CommonModule } from '@angular/common';
import { MaxLengthNumberDirective } from '../../../directives/max-length-number.directive';

@Component({
  selector: 'p-input',
  templateUrl: './p-input.component.html',
  styleUrls: ['./p-input.component.scss'],
  standalone: true,
  imports: [IconsModule, CommonModule, FormsModule, MaxLengthNumberDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => PInputComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PInputComponent implements ControlValueAccessor {
  @Input() formControl!: FormControl;
  @Input() name!: string;
  @Input() label!: string;
  @Input() type: 'email' | 'text' | 'number' | 'password' | 'tel' | 'date' =
    'text';
  @Input() size: 'large' | 'small' | 'default' = 'default';
  @Input() description!: string;
  @Input() autocomplete!: string;
  @Input() disabled!: boolean;
  @Input() hideRequiredStar!: boolean;
  @Input() withRightIcon = false;
  @Input() helpText!: string | undefined;
  @Input() helpTextRight!: string | undefined;
  @Input() placeholder!: string;
  @Input() fixedText!: string;
  @Input() tabindex!: number;
  @Input() maxLength!: number;
  @Input() showCounter = false;
  @Input() maxLengthNumber!: number;
  @Input() required = false;
  // Le message d'erreur qui apparaitera si le from control n'est pas valide.
  // Pour l'instant, le composant ne gère pas plusieurs messages d'erreurs, on fera évoluer cet input si besoin.
  @Input() errorMessage!: string;
  @Input() iconPointer = true;

  @Output() inputBlur = new EventEmitter<string>();
  @Output() inputChange = new EventEmitter<string>();

  onChanged!: (value: string) => void;
  onTouched!: () => void;
  value!: string;
  showError = false;
  showToolTipText = false;

  constructor(private readonly changeDetector: ChangeDetectorRef) {}

  toggleTooltip() {
    this.showToolTipText = !this.showToolTipText;
  }

  hideTooltip() {
    this.showToolTipText = false;
  }

  writeValue(value: string): void {
    if (this.formControl.value) {
      this.value = this.formControl.value;
    }

    this.value = value;
    this.changeDetector.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  markAsTouched(): void {
    this.onTouched();
  }

  preventInvalidKey(event: KeyboardEvent): void {
    if (this.type !== 'number') {
      return;
    }

    const invalidKeys = ['e', 'E', '+', '-'];
    if (invalidKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  handleChange(value: string): void {
    this.value = value;
    this.onChanged(value);
    this.inputChange.emit(value);
  }

  handleBlur(): void {
    this.markAsTouched();
    this.showError = !this.formControl.valid;
    this.inputBlur.emit(this.value);
  }

  // Pour rendre le form control obligatoire (required),
  // il faut absolument lui attribué le validateur Angular Validators.required dans le composant parent.
  get isRequired(): boolean {
    return this.required || this.formControl.hasValidator(Validators.required);
  }

  get classes(): ngClassObject<string> {
    return {
      'form-control-lg': this.size === 'large',
      'form-control-sm': this.size === 'small',
    };
  }
}
